import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  list: {
    background: theme.palette.background.paper,
  },
  label: {
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  /* Copied styling from clinicDetails to render TextField form look */
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#F79500',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
  },
  textfieldColor: {
    '& .MuiInputBase-input': {
      color: '#002D55',
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)'
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)'
    }
  },
  textfieldSpacing: {
    marginBottom: 8,
  },
  responsiveText: {
    fontSize: 'clamp(0.7875rem, 1.26vw, 0.85rem)'
  },
  Button2: {
    transition: 'background 0.2s',
    backgroundColor: '#002D55',
    color: '#FFFF',
    zIndex: 2,
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#F79500',
      fontWeight: 'bold',
    },
  }
}))

export default function CareProviderDetails({ careProviderId, careProvider, onBack }) {
  const classes = useStyles()
  const token = useSelector(state => state.auth && state.auth.token)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(careProvider || null)

  useEffect(() => {
    let cancelled = false
    if (!careProviderId) return
    // If careProvider already passed via prop, use it
    if (careProvider) {
      setData(careProvider)
      return
    }

    const fetchDetails = async () => {
      setLoading(true)
      setError(null)
      try {
        // Try to fetch a single-careprovider endpoint first (if implemented).
        let res = await fetch(`https://api.virtus-one.com/profile/careprovider/${careProviderId}/`, {
          headers: { Authorization: token ? `Token ${token}` : '', Accept: 'application/json' }
        })

        if (res.ok) {
          const payload = await res.json()
          // payload may be the object or { care_provider: obj }
          const cp = payload.care_provider || payload
          if (!cancelled) setData(cp || null)
          return
        }

        // Fallback: fetch the full list and find the care provider
        res = await fetch('https://api.virtus-one.com/profile/careproviders/', {
          headers: { Authorization: token ? `Token ${token}` : '', Accept: 'application/json' }
        })
        if (!res.ok) throw new Error(`Failed to fetch care providers: ${res.status}`)
        const payload = await res.json()
        const list = Array.isArray(payload) ? payload : (payload.care_providers || [])
        const found = list.find(cp => String(cp.id) === String(careProviderId))
        if (!cancelled) setData(found || null)
      } catch (err) {
        if (!cancelled) setError(err.message || String(err))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchDetails()
    return () => { cancelled = true }
  }, [careProviderId, careProvider, token])

  if (loading) return <CircularProgress size={20} />

  if (error) return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6">Care Provider</Typography>
        {onBack ? <Button className={classes.Button2} onClick={onBack}>Back</Button> : null}
      </div>
      <Typography color="error">{error}</Typography>
    </Paper>
  )

  if (!data) return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6">Care Provider</Typography>
        {onBack ? <Button className={classes.Button2} onClick={onBack}>Back</Button> : null}
      </div>
      <Typography color="textSecondary">No details available for this care provider.</Typography>
    </Paper>
  )

  const rows = [
    ['Business name', data.name_business || data.name || '-'],
    ['Type', data.care_provider_type || data.type || '-'],
    ['City', data.city || '-'],
    ['Postal code', data.postal_code || data.postalCode || '-'],
    ['Country', data.country || '-'],
    ['Address', data.address || '-'],
    ['Phone', data.phoneNumber_CareProvider || data.phone || '-'],
    ['Email', data.email_CareProvider || data.email || '-'],
    ['Specialties', (data.specialties && data.specialties.join) ? data.specialties.join(', ') : (Array.isArray(data.specialties) ? data.specialties.join(', ') : (data.specialty || '-'))],
    ['Services', (data.services && data.services.length) ? data.services.map(s => {
      if (typeof s === 'string') return s
      return `${s.name || s.title || ''}${s.price ? ` — ${s.price}${s.currency ? ' ' + s.currency : ''}` : ''}`
    }).join('\n') : '-'],
    ['Supports insurance', data.supports_insurance ? 'Yes' : 'No'],
    ['Public subsidy', data.public_subsidy ? 'Yes' : 'No'],
    ['Average NPS', data.avg_nps !== undefined && data.avg_nps !== null ? String(data.avg_nps) : '-']
  ]

  // Additional admin/metadata fields
  const adminRows = [
    ['Business registration', data.business_registration_number || '-'],
    ['Main contact first name', data.firstName_mainContactPerson || '-'],
    ['Main contact last name', data.lastName_mainContactPerson || '-'],
    ['Contact phone', data.phoneNumber_ContactPerson || '-'],
    ['Contact email', data.email_contactPerson || '-'],
    ['Notes', data.notes || '-'],
    ['Created at', data.created_at ? (new Date(data.created_at).toLocaleString()) : '-'],
    ['Updated at', data.updated_at ? (new Date(data.updated_at).toLocaleString()) : '-']
  ]

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6">Care Provider Details</Typography>
        {onBack ? <Button className={classes.Button2} onClick={onBack}>Back</Button> : null}
      </div>
      <Divider style={{ marginBottom: 12 }} />
      <Grid container spacing={2}>
        {rows.map(([label, val]) => (
          <Grid item xs={12} md={6} key={label}>
            <TextField
              label={label}
              value={val !== undefined && val !== null ? String(val) : ''}
              variant="outlined"
              fullWidth
              disabled
              InputLabelProps={{ shrink: true }}
              className={clsx(classes.orangeFocused, classes.textfieldColor, classes.textfieldSpacing)}
              multiline={label === 'Services'}
              rows={label === 'Services' ? 3 : 1}
            />
          </Grid>
        ))}

        {/* admin/meta fields */}
        {adminRows.map(([label, val]) => (
          <Grid item xs={12} md={6} key={label}>
            <TextField
              label={label}
              value={val !== undefined && val !== null ? String(val) : ''}
              variant="outlined"
              fullWidth
              disabled
              InputLabelProps={{ shrink: true }}
              className={clsx(classes.orangeFocused, classes.textfieldColor, classes.textfieldSpacing)}
              multiline={label === 'Notes'}
              rows={label === 'Notes' ? 3 : 1}
            />
          </Grid>
        ))}
      </Grid>
    </Paper>
  )
}
